import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { InterfaceService, InterfaceStatusType } from '@/types';

import InterfaceStatusBadge from '@/components/interface-status/interface-status-badge';
import StatusIndicator from '@/components/interface-status/status-indicator';
import { Separator } from '@/components/ui/separator';

interface InterfaceStatusCardProps {
  data: InterfaceStatusType;
}

const byServiceName = (a: InterfaceService, b: InterfaceService) =>
  a.serviceName.localeCompare(b.serviceName);

const InterfaceStatusCard: FC<InterfaceStatusCardProps> = ({ data }) => {
  const { t } = useTranslation();

  const { status, coreServices, secondaryServices } = data;

  const serviceGroups = [
    {
      id: 'core',
      label: t('interface_status.core_service'),
      items: coreServices.flatMap((service) => service.services),
    },
    {
      id: 'secondary',
      label: t('interface_status.secondary_services'),
      items: secondaryServices.flatMap((service) => service.services).sort(byServiceName),
    },
  ];

  return (
    <>
      <InterfaceStatusBadge
        status={status}
        className="shrink-0"
        badgeClassName="max-w-52 whitespace-normal"
      />
      <Separator className="shrink-0" />
      {serviceGroups.map(({ id, label: groupLabel, items }, index) => {
        const showSeparator = index > 0;

        return (
          <div key={id}>
            {showSeparator && <Separator className="mb-3 shrink-0" />}
            <p className="mb-2 text-base font-semibold text-foreground">{groupLabel}</p>
            <div className="flex flex-col gap-2">
              {items.map(({ serviceName, status: itemStatus }) => (
                <div key={serviceName} className="flex items-center gap-2">
                  <StatusIndicator status={itemStatus} className="size-3" />
                  <span className="text-sm text-foreground">{serviceName}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default InterfaceStatusCard;
